import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Department } from '../models/department';
import { DepartmentService } from '../services/department.service';

/**
 * Department data source
 */
export class DepartmentDataSource implements DataSource<Department> {
  /**
   * Department subject
   *
   * @private
   */
  private departmentsSubject = new BehaviorSubject<Department[]>([]);

  /**
   * Loading subject
   *
   * @private
   */
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Loading observable
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Constructor
   *
   * @param departmentService  service
   */
  constructor(private departmentService: DepartmentService) {}

  /**
   * Connect to collection
   *
   * @param collectionViewer CollectionViewer
   *
   * @returns Observable
   */
  connect(_collectionViewer: CollectionViewer): Observable<Department[]> {
    return this.departmentsSubject.asObservable();
  }

  /**
   * Disconnect from collection
   *
   * @param collectionViewer CollectionViewer
   */
  disconnect(_collectionViewer: CollectionViewer): void {
    this.departmentsSubject.complete();
    this.loadingSubject.complete();
  }

  /**
   * Load data from collection
   *
   * @param order Order
   * @param sort Sort
   * @param limit Limit
   * @param page Page
   * @param filter Object with filtered attributes
   */
  load(order: string = 'description', sort: string = 'asc', limit: number = 5, page: number = 0, filter: object = {}): void {
    this.loadingSubject.next(true);

    const options = { order, sort, limit, page };

    for (const i in filter) {
      if (filter.hasOwnProperty(i) && filter[i] === '') {
        delete filter[i];
      }
    }

    this.departmentService
      .findFiltered({ ...options, ...filter })
      .pipe(
        catchError(() => of([])),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((departments) => this.departmentsSubject.next(departments));
  }
}
